import React, { useEffect, useState } from 'react';
import { Button, Card, CardContent, Chip, Grid, Tab, Tabs, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Page from '../../../components/page/page';
import PageHeader from '../../../components/page/pageHeader';
import { NonAuthRoutes } from '../../../model';
import { BalanceModel, BalanceResponsiveModel, StatusBalanceResponse } from '../balances.model';
import { useHttpError } from '../../../hooks/useHttpError';
import { fetchBalanceData, fetchBalances, getStatusBalance } from '../balances.services';
import moment from 'moment';
import {
  DataTableColumn,
  DataType,
  FormDialog,
  FormDialogMode,
  FormSkeleton,
  useNotify,
  OnEventReceived
} from '@globaleris/ui.library';
import { calculateResponsiveBalance } from './balance.service';
import { useSelector } from 'react-redux';
import { RootStoreModel } from '../../../redux';
import { DatePeriod, GlobalNotification } from '../../../model';
import { NotificationMessageIcons } from '../../../components';

export const BalanceResponsivePage: React.FC = () => {
  const [t] = useTranslation(['globalerisLibrary', 'translation', 'balance']);
  const history = useHistory();

  const [isFetching, setIsFetching] = React.useState<boolean>(true);
  const [activeTab, setActiveTab] = React.useState(0);
  const [balances, setBalances] = React.useState<BalanceModel[] | null>(null);
  const [activeBalance, setActiveBalance] = React.useState<BalanceResponsiveModel | null>(null);
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<Date>(moment().toDate());
  const [fistInit, setFistInit] = React.useState<boolean>(true);
  const [dataForm, setDataForm] = React.useState<any>(undefined);

  const { showError, clearError, errorProps } = useHttpError();
  const { info, getEventsReceived, addEventListener, deleteEventListener } = useNotify();

  const [balanceStatus, setBalanceStatus] = useState<StatusBalanceResponse>({inProcess: false}); 

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  );

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  );

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined);

  //#region Form Columns

  const columns: DataTableColumn[] = [
    {
      name: 'date',
      label: 'Fecha',
      type: DataType.DATE,
      required: true,
      readOnly: false,
      defaultValue: moment().toDate()
    }
  ];

  //#endregion

  //#region Handlers

  const onChangeTabHandler = (event: React.ChangeEvent<{}>, tab: number) => {
    activeTab !== tab && setActiveTab(tab);
  };

  const onClickTabHandler = async (balance: BalanceModel) => {
    getBalanceData(balance);
  };

  const handleFormClose = (modifiedDate: any) => {
    modifiedDate === undefined ? setOpenDialog(false) : setDate(moment(modifiedDate.date).toDate());
    setFistInit(false);
  };

  //#endregion

  //#region Effects

  useEffect(() => {
    getBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    balances && balances.length > 0 && getBalanceData(balances[0]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances]);

  useEffect(() => {
    balances && balances.length > 0 && getBalanceData(balances[0]);
    setDataForm({ date: date });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, activeCompany, activeBalanceZone]);

  //#endregion

  //#region Functions

  const getBalances = async () => {
    setBalances(
      await fetchBalances().then(response => {
        if (response.success) {
          clearError();
          return response.data;
        } else {
          showError({
            title: t('balance:title'),
            errorCode: response.error!.code,
            onRefresh: getBalances
          });
          return null;
        }
      })
    );
  };

  const getBalanceData = async (balance: BalanceModel) => {
    setIsFetching(true);
    setActiveBalance(null);
    setOpenDialog(false);
    // eslint-disable-next-line eqeqeq
    if (balance.id != 1) {
      setActiveBalance(
        await fetchBalanceData(balance.id, moment(date).toDate(), moment(date).toDate()).then(
          response => {
            setIsFetching(false);

            if (response.success) {
              clearError();
              if (!balanceStatus.inProcess)
                 setGlobalNotification(undefined);
              if (!response.data) return null;
                 
              setBalanceStatus((prevState) => ({...prevState, displayTime : new Date()}));
              return calculateResponsiveBalance(balance, response.data, moment(date).toDate());
            } else {
              showError({
                title: t('balance:title'),
                errorCode: response.error!.code,
                onRefresh: getBalances
              });
              return null;
            }
          }
        )
      );
    } else {
      setActiveBalance(
        // balance.id, moment().toDate(), moment().toDate()
        await fetchBalanceData(
          balance.id,
          moment(date).toDate(),
          moment(date).add(1, 'days').toDate()
        ).then(response => {
          setIsFetching(false);

          if (response.success) {
            clearError();
            if (!response.data) return null;
              setBalanceStatus((prevState) => ({...prevState, displayTime : new Date()}));
            return calculateResponsiveBalance(balance, response.data, moment(date).toDate());
          } else {
            showError({
              title: t('balance:title'),
              errorCode: response.error!.code,
              onRefresh: getBalances
            });
            return null;
          }
        })
      );
    }
  };

    //Eventos
  useEffect(() => {
    //getEventsReceived, addEventListener, deleteEventListener
    const eventReceived = (event: string, date: Date, data: any) => {

      switch (event) {
        case 'UpdatedBalance': 
          setBalanceStatus((prevState) => ({
            ...prevState,
            inProcess: false,
            lastExecutionTime: date
          }));
         break;
        case 'UpdatingBalance': 
          setBalanceStatus((prevState) => ({
            ...prevState,
            inProcess: true,
            lastExecutionTime: date
          }));
         break;
      }
    }

    addEventListener("Balance", [], 
      { onEventReceivedHandler: eventReceived } as OnEventReceived);

    const lastEventReceived = getEventsReceived(['UpdatedBalance', 'UpdatingBalance', 'UpdateFailedBalance']);
    if (lastEventReceived && lastEventReceived?.length > 0) {

      switch (lastEventReceived[0].event) {
        case 'UpdatedBalance': 
          setBalanceStatus((prevState) => ({
            ...prevState,
            inProcess: false,
            lastExecutionTime: lastEventReceived[0].date
          }));
         break;
        case 'UpdatingBalance': 
          setBalanceStatus((prevState) => ({
            ...prevState,
            inProcess: true,
            lastExecutionTime: lastEventReceived[0].date
          }));
         break;
      }

      //console.log('Último evento recibido ' + lastEventReceived[0].event + ' fecha: ' + lastEventReceived[0].date);
    }
    else {
      getStatusBalance().then((status) => {

        setBalanceStatus((prevState) => ({
          ...prevState,
          inProcess: status?.inProcess ?? false,
          lastExecutionTime: status?.lastExecutionTime
        }));
      });
    }

    return () => { deleteEventListener();}

  }, []);

  useEffect(() => {
      if (balanceStatus && balanceStatus.lastExecutionTime){
        if (balanceStatus.inProcess){
              setGlobalNotification({
              icon: NotificationMessageIcons.LOADING,
              message: t('balance:globalNotifications.calculation.messages.onCalculation')
              });
        }
        else if ((balanceStatus.displayTime ?? moment().toDate()) < balanceStatus.lastExecutionTime){
          setGlobalNotification({
            icon: NotificationMessageIcons.ERROR,
            message: t('balance:globalNotifications.calculation.messages.onFinish'),
            actions: [
              {
                label: t('balance:globalNotifications.calculation.actions.refresh'),
                onClick: async () => {
                  getBalances();
                }
              }
            ]
          });
        }
        else {
          setGlobalNotification(undefined)
        }
      }
  }, [balanceStatus]);

  //#endregion
  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('balance:title')}>
        <Chip
          size="small"
          color={'primary'}
          clickable={true}
          label={
            fistInit
              ? t('translation:components.dateFilter.today')
              : date.getDate() + '/' + `${date.getMonth() + 1}` + '/' + date.getFullYear()
          }
          onClick={() => {
            setOpenDialog(true);
          }}
        ></Chip>
        <FormDialog
          title={t('balance:responsive:form.title')}
          inputs={columns}
          inputData={dataForm}
          mode={FormDialogMode.EDIT}
          onClose={handleFormClose}
          open={openDialog}
          okBtnLabel={t('balance:responsive:form.btnOK')}
          cancelBtnLabel={t('balance:responsive:form.btnKO')}
        />
      </PageHeader>
      <Tabs
        value={activeTab}
        onChange={onChangeTabHandler}
        aria-label="simple tabs example"
        indicatorColor="primary"
        textColor="primary"
      >
        {balances &&
          balances.map((balance, index) => {
            return (
              <Tab
                key={index}
                label={balance.description}
                // {...a11yProps(index)}
                onClick={() => {
                  onClickTabHandler(balance);
                }}
              />
            );
          })}
      </Tabs>

      <Grid container spacing={3}>
        <Grid item container>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                {activeTab !== 0 ? (
                  <Typography variant="subtitle1">
                    {t('balance:responsive:labels.stock')}
                  </Typography>
                ) : (
                  <Typography variant="subtitle1">
                    {t('balance:responsive:labels.demand')}
                  </Typography>
                )}

                {activeBalance && !isFetching ? (
                  <>
                    <Typography variant="h2" gutterBottom>
                      {activeBalance && activeBalance.stock} kWh
                    </Typography>
                    <Typography variant="subtitle2">
                      {activeTab !== 0 ? (
                        <Chip size="small" label={t('balance:responsive:labels.yesterday')}></Chip>
                      ) : (
                        <Chip size="small" label={t('balance:responsive:labels.tomorrow')}></Chip>
                      )}
                      {activeTab !== 0
                        ? activeBalance && activeBalance.previousStock
                        : activeBalance && activeBalance.followingStock}
                      kWh
                    </Typography>
                  </>
                ) : (
                  <>
                    <FormSkeleton columns={1} inputs={1}></FormSkeleton>
                    <FormSkeleton columns={1} inputs={1}></FormSkeleton>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1">
                  {t('balance:responsive.labels.unbalance')}
                </Typography>
                {activeBalance && !isFetching ? (
                  <>
                    <Typography variant="h2">
                      {activeBalance && activeBalance.unbalance} kWh
                    </Typography>
                  </>
                ) : (
                  <FormSkeleton columns={1} inputs={1}></FormSkeleton>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={12}>
            <Button
              size="small"
              color="primary"
              onClick={() => {
                history.push(NonAuthRoutes.nomination);
              }}
            >
              {t('balance:responsive.actions.navigateLogisticOperations')}
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => {
                history.push(NonAuthRoutes.buyAndSell);
              }}
            >
              {t('balance:responsive.actions.navigateBuyAndSellOperations')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Page>
  );
};
